import Fuse from "fuse.js";

const MAX_ITERATIONS = 16;
class Assignable {
	from(obj: any) {
		Object.assign(this, obj);
	}
	public static from(obj: any) {
		let o = new this();
		o.from(obj);
		return o;
	}
}
class Bilingual<T = string> extends Assignable {
	en: T;
	fr: T;
	_v: T;
	constructor(en?: T, fr?: T) {
		super();
		if (en == fr) {
			this._v = en;
		}
		this.en = en;
		this.fr = fr;
	}

	value(language: "en" | "fr") {
		if (this._v) {
			return this._v;
		}
		switch (language) {
			case "en":
				return this.en;
			case "fr":
				return this.fr;
			default:
				return this.en;
		}
	}
	public static from<T = string>(obj: any) {
		let o = new this<T>();
		o.from(obj);
		return o;
	}
}

class Station extends Assignable {
	code: Bilingual = new Bilingual("", "");
	lat: Bilingual = new Bilingual("", "");
	lon: Bilingual = new Bilingual("", "");
	value: Bilingual = new Bilingual("", "");
	from(obj: any) {
		super.from(obj);
		this.code = DataPoint.from(this.code);
		this.lat = DataPoint.from(this.lat);
		this.lon = DataPoint.from(this.lon);
		this.value = DataPoint.from(this.value);
	}
}

class DataPoint<T = number> extends Assignable {
	unitType: Bilingual = new Bilingual("", "");
	units: Bilingual = new Bilingual("", "");
	qaValue: Bilingual<T>;
	value: Bilingual<T>;
	change: Bilingual<number>;
	tendency: Bilingual;
	class: Bilingual;
	from<T=number>(obj: any) {
		super.from(obj);
		this.unitType = Bilingual.from(this.unitType);
		this.units = Bilingual.from(this.units);
		this.qaValue = Bilingual.from(this.qaValue);
		this.value = Bilingual.from(this.value);
		this.change = Bilingual.from(this.change);
		this.tendency = Bilingual.from(this.tendency);
	}
	/* public static from(obj: any) {
		let o = new this();
		o.from(obj);
		return o;
	} */
	public get Value(): string {
		return `${this.value.en} ${this.units.en}`.trim();
	}
}
class Wind extends Assignable {
	bearing: DataPoint;
	direction: DataPoint;
	gust: DataPoint;
	speed: DataPoint;
	from(obj: any) {
		super.from(obj);
		this.bearing = DataPoint.from(this.bearing);
		this.direction = DataPoint.from(this.direction);
		this.gust = DataPoint.from(this.gust);
		this.speed = DataPoint.from(this.speed);
	}
}

class Warning extends Assignable {
	alertColorLevel: Bilingual;
	description: Bilingual<Date | string>;
	eventIssue: Bilingual<Date | string>;
	expiryTime: Bilingual<Date | string>;
	priority: Bilingual;
	type: Bilingual;
	url: Bilingual;
	from(obj: any) {
		super.from(obj);
		this.alertColorLevel = Bilingual.from(this.alertColorLevel);
		this.description = Bilingual.from(this.description);
		this.eventIssue = Bilingual.from(this.eventIssue);
		this.expiryTime = Bilingual.from(this.expiryTime);
		this.priority = Bilingual.from(this.priority);
		this.type = Bilingual.from(this.type);
		this.url = Bilingual.from(this.url);
		return this;
	}
	/* public static from(obj: any) {
		let o = new this();
		o.from(obj);
		return o;
	} */
}
export class Conditions extends Assignable {
	timestamp: Bilingual<Date | string> = new Bilingual(new Date(), new Date());
	condition: Bilingual<string> = new Bilingual("", "");

	// Datapoints
	dewpoint: DataPoint;
	temperature: DataPoint;
	windChill: DataPoint;
	pressure: DataPoint;
	relativeHumidity: DataPoint;

	wind: Wind;

	station: Station;

	iconCode: {format: string, value: number, url: string};
	from(obj: any) {
		super.from(obj);
		this.timestamp = Bilingual.from(this.timestamp);
		this.condition = Bilingual.from(this.condition);
		this.dewpoint = DataPoint.from(this.dewpoint);
		this.temperature = DataPoint.from(this.temperature);
		this.windChill = DataPoint.from(this.windChill);
		this.pressure = DataPoint.from(this.pressure);
		this.relativeHumidity = DataPoint.from(this.relativeHumidity);
		this.wind = Wind.from(this.wind);
		this.station = Station.from(this.station);
	}
	/* public static from(obj: any) {
		let o = new this();
		o.from(obj);
		return o;
	} */
}

class ForecastGroup {
	timestamp: Bilingual<Date>;
	// textSummary: Bilingual;
	regionalNormals: {
		textSummary: Bilingual,
		temperature: Array<DataPoint>
	}
}
class Forecast {

}
class City extends Assignable {
	language: "en" | "fr" = "en";
	loaded: boolean = false;
	currentConditions: Conditions;
	identifier: string;
	lastUpdated: string | Date;
	name: Bilingual;
	region: Bilingual;
	url: Bilingual;
	warnings: Array<Warning> = [];
	
	from(obj: Object): void {
		super.from(obj);
		this.currentConditions = Conditions.from(this.currentConditions);
		this.name = Bilingual.from(this.name);
		this.region = Bilingual.from(this.region);
		this.url = Bilingual.from(this.url);
	}
	
	
	public get Temperature() : string {
		return this.currentConditions.temperature.Value;
	}
	public get FeelsLike(): string {
		let new_temp = this.currentConditions.temperature.value[this.language] + this.currentConditions.windChill.value[this.language]
		return `${new_temp.toFixed(1)} ${this.currentConditions.temperature.units[this.language]}`;
	}
	public get image() : string {
		return this.currentConditions.iconCode.url;
	}
	public get forecasts() {
		return this.forecastGroup.forecasts.map(f => {return {period: f.period, summary: f.textSummary};});
	}
}

class Feature {
	type = "Feature";
	properties!: City;
	id: string = "";
}
export class Weather {
	static base_url =
		"https://api.weather.gc.ca/collections/citypageweather-realtime/";
	language: "en" | "fr" = "en";
	features: Array<Feature> = [];
	features_loaded: boolean = false;
	fuse: Fuse<Feature>;
	cities: Map<string, City> = new Map();
	constructor(language: "en" | "fr" = "en") {
		this.language = language;
	}
	async _fetch(endpoint: string, init?: RequestInit) {
		return fetch(Weather.base_url + endpoint).then((res) => {
			if (res.ok) {
				return res.json();
			}
			console.log(res);
			console.error(res.status);
			console.error(res.statusText);
			throw new Error(`Failed to get features: ${endpoint}`);
		});
	}
	async _getFeatures(
		offset: number = 0,
	): Promise<{ features: Array<Feature>; done: boolean }> {
		let URL = `items?&f=json&offset=${offset}`; // TODO: move to a constants file or something;
		return this._fetch(URL).then((res) => {
			// features.push(...res.features);
			return {
				features: res.features,
				done: res.numberReturned + offset >= res.numberMatched,
			};
		});
	}
	async updateFeatures() {
		let features = [];
		let done = false;
		console.log("updating features");
		for (let index = 0; !done && index < MAX_ITERATIONS; index++) {
			console.log(`Current index: ${index}`);
			let out = await this._getFeatures(features.length);
			features.push(
				...out.features.map((f) => {
					f.properties = City.from(f.properties);
					f.properties.language = this.language;
					return f;
				}),
			);
			done = out.done;
		}
		this.features = features;
		this.features_loaded = true;
		// this.cities = this.features.map((f) => {});
		this.features.forEach((e) => this.cities.set(e.id, e.properties));
		return this.features;
	}

	async get(id: string): Promise<City> {
		if (this.features_loaded && this.cities.has(id)) {
			return this.cities.get(id);
		}
		await this._fetch(`items/${id}?f=json`).then((city) => {
			let c = City.from(city.properties);
			// console.log(city)
			// console.log(c)
			this.cities.set(id, c);
		});
		return this.cities.get(id);
	}

	async Features(): Promise<Feature[]> {
		if (this.features_loaded) {
			return this.features;
		}
		// update features
		return await this.updateFeatures();
		// maybe throw error if features isn't called
	}

	// TODO: implement searching. For now, you just need to know your id.
	/* async search(query){
		if (!this.fuse){
			if (!this.features){
				await this.updateFeatures();
			}
			this.fuse = new Fuse(this.features)

		}
	} */
}

export default Weather;
